/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/Materials/MaterialOverview/AdminDownloadDatasetModal.tsx.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle, Download, X } from "lucide-react";
import { downloadAdminMaterialDataset } from "../../../../../services/adminDatasetExportService";
import "./AdminDownloadDatasetModal.css";

type ModalPhase = "confirm" | "loading" | "success" | "error";

interface Props {
  open: boolean;
  onClose: () => void;
  materialId: number;
  materialName: string;
  submissionCount: number;
}

const AdminDownloadDatasetModal: React.FC<Props> = ({
  open,
  onClose,
  materialId,
  materialName,
  submissionCount,
}) => {
  const [phase, setPhase] = useState<ModalPhase>("confirm");
  const [error, setError] = useState<string | null>(null);
  const [resultFilename, setResultFilename] = useState<string | null>(null);
  const [exportedCount, setExportedCount] = useState<number | null>(null);
  const startedRef = useRef(false);

  const reset = useCallback(() => {
    setPhase("confirm");
    setError(null);
    setResultFilename(null);
    setExportedCount(null);
    startedRef.current = false;
  }, []);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const runDownload = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setPhase("loading");
    setError(null);

    try {
      const result = await downloadAdminMaterialDataset(materialId);
      setResultFilename(result.filename);
      setExportedCount(result.rowCount);
      setPhase("success");
    } catch (err: unknown) {
      const msg =
        (err as { message?: string })?.message ||
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Failed to generate the dataset export. Please try again.";
      setError(msg);
      setPhase("error");
      startedRef.current = false;
    }
  }, [materialId]);

  const handleClose = () => {
    if (phase === "loading") return;
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="admin-dataset-download-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-dataset-download-title"
    >
      <div className="admin-dataset-download-modal">
        <div className="admin-dataset-download-header">
          <div>
            <h2 id="admin-dataset-download-title">Download submission dataset</h2>
            <p className="admin-dataset-download-subtitle">
              Material: <strong>{materialName || `#${materialId}`}</strong>
              {submissionCount > 0 ? (
                <>
                  {" "}
                  · {submissionCount} submission{submissionCount === 1 ? "" : "s"}
                </>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            className="admin-dataset-download-close"
            onClick={handleClose}
            disabled={phase === "loading"}
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>

        <div className="admin-dataset-download-body">
          {phase === "loading" ? (
            <div className="admin-dataset-download-loading" role="status" aria-live="polite">
              <div className="admin-dataset-download-spinner" aria-hidden="true" />
              <p className="admin-dataset-download-loading-title">Preparing your export…</p>
              <p className="admin-dataset-download-loading-text">
                This may take a minute for materials with many submissions. Please keep this
                window open until the download starts.
              </p>
            </div>
          ) : null}

          {phase === "success" ? (
            <div className="admin-dataset-download-result admin-dataset-download-result--success">
              <CheckCircle size={28} aria-hidden="true" />
              <div>
                <p className="admin-dataset-download-result-title">Download ready</p>
                <p className="admin-dataset-download-result-text">
                  {resultFilename ? (
                    <>
                      <strong>{resultFilename}</strong> has been saved to your downloads folder.
                    </>
                  ) : (
                    "Your export has been saved to your downloads folder."
                  )}
                  {exportedCount != null ? (
                    <> {exportedCount} row{exportedCount === 1 ? "" : "s"} exported.</>
                  ) : null}
                </p>
              </div>
            </div>
          ) : null}

          {phase === "error" ? (
            <div className="admin-dataset-download-result admin-dataset-download-result--error">
              <AlertTriangle size={28} aria-hidden="true" />
              <div>
                <p className="admin-dataset-download-result-title">Export failed</p>
                <p className="admin-dataset-download-result-text">{error}</p>
              </div>
            </div>
          ) : null}

          {(phase === "confirm" || phase === "error") && (
            <>
              <div className="admin-dataset-download-notes">
                <p className="admin-dataset-download-notes-title">Before you download</p>
                <ul>
                  <li>
                    The file is a single-sheet flat export (Admin data dwd template) with one row
                    per submission and summary embodied energy and carbon values for A1, A2, A3, and
                    A1–A3.
                  </li>
                  <li>
                    All submissions linked to this material are included, regardless of review
                    status.
                  </li>
                  <li>
                    This format is for internal analysis only — not for contributor upload.
                  </li>
                  <li>
                    Handle the downloaded file securely; it may contain contributor and plant
                    location data.
                  </li>
                  <li>
                    Large materials can produce a sizable file; generation runs on the server and
                    may take longer when many submissions exist.
                  </li>
                </ul>
              </div>

              {submissionCount === 0 ? (
                <p className="admin-dataset-download-empty-warning" role="alert">
                  There are no submissions for this material yet. The export will contain headers
                  only.
                </p>
              ) : null}
            </>
          )}
        </div>

        <div className="admin-dataset-download-actions">
          {phase === "loading" ? (
            <button type="button" className="admin-dataset-download-secondary" disabled>
              Generating…
            </button>
          ) : phase === "success" ? (
            <button type="button" className="admin-dataset-download-primary" onClick={handleClose}>
              Close
            </button>
          ) : phase === "error" ? (
            <>
              <button type="button" className="admin-dataset-download-secondary" onClick={handleClose}>
                Cancel
              </button>
              <button
                type="button"
                className="admin-dataset-download-primary"
                onClick={() => void runDownload()}
              >
                <Download size={16} aria-hidden="true" />
                Retry download
              </button>
            </>
          ) : (
            <>
              <button type="button" className="admin-dataset-download-secondary" onClick={handleClose}>
                Cancel
              </button>
              <button
                type="button"
                className="admin-dataset-download-primary"
                onClick={() => void runDownload()}
              >
                <Download size={16} aria-hidden="true" />
                Start download
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/** React component — Admin Download Dataset Modal. */
export default AdminDownloadDatasetModal;
